import React, { createContext, useContext, useState, useEffect } from 'react';
import { dataFetch } from '../api';

const SessionContext = createContext();

export const useSessionContext = () => {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSessionContext must be used within SessionProvider');
  return context;
};

export const SessionProvider = ({ children }) => {
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    const loadSessions = async () => {
      const userId = localStorage.getItem('user_id');
      if (!userId) return;
      
      const res = await dataFetch(`read/sessions?owner_id=${userId}`);
      if (res?.status === 'success') {
        const loaded = (res.data ?? []).map(s => {
          let parsedTechniques = [];
          if (s.techniques) {
            try {
              parsedTechniques = typeof s.techniques === 'string' ? JSON.parse(s.techniques) : s.techniques;
            } catch (e) {
              console.error("Failed to parse techniques on load", e);
            }
          }
          
          return {
            ...s,
            // Map lowercase API column back to camelCase for the frontend
            sessionType: s.sessiontype || s.sessionType || 'Rolling',
            duration: Number(s.duration) || 0,
            wins: Number(s.wins) || 0,
            losses: Number(s.losses) || 0,
            techniques: Array.isArray(parsedTechniques) ? parsedTechniques : []
          };
        });
        setSessions(loaded);
      }
    };
    loadSessions();
  }, []);

  const addSession = async (sessionData) => {
    const userId = localStorage.getItem('user_id');
    const newSession = {
      ...sessionData,
      // Map camelCase to lowercase for the API to recognize it
      sessiontype: sessionData.sessionType || 'Rolling',
      duration: Number(sessionData.duration) || 0,
      wins: Number(sessionData.wins) || 0,
      losses: Number(sessionData.losses) || 0,
      techniques: JSON.stringify(sessionData.techniques || []),
      user_id: userId
    };
    
    // Clean up payload so we don't send unused camelCase fields
    delete newSession.sessionType;

    const res = await dataFetch(`create/sessions?owner_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(newSession)
    });
    
    if (res?.status === 'success') {
      // Merge what the user entered with the backend response (which provides the new ID)
      const created = {
        ...sessionData,
        ...(res.data || {})
      };
      
      created.sessionType = created.sessiontype || created.sessionType || sessionData.sessionType || 'Rolling';
      created.duration = Number(created.duration) || Number(sessionData.duration) || 0;
      created.wins = Number(created.wins) || Number(sessionData.wins) || 0;
      created.losses = Number(created.losses) || Number(sessionData.losses) || 0;
      
      let parsedTechniques = sessionData.techniques || [];
      if (res.data?.techniques && typeof res.data.techniques === 'string') {
        try { 
          parsedTechniques = JSON.parse(res.data.techniques); 
        } catch(e) {
          console.error("Failed to parse techniques on create", e);
        }
      } else if (Array.isArray(res.data?.techniques)) {
        parsedTechniques = res.data.techniques;
      }
      created.techniques = parsedTechniques;

      setSessions(prev => [created, ...prev]);
      return created;
    }
  };

  const updateSession = async (id, sessionData) => {
    const userId = localStorage.getItem('user_id');
    const dataToUpdate = { ...sessionData };
    
    if (dataToUpdate.techniques) {
      dataToUpdate.techniques = JSON.stringify(dataToUpdate.techniques);
    }
    
    if (dataToUpdate.sessionType) {
      dataToUpdate.sessiontype = dataToUpdate.sessionType;
      delete dataToUpdate.sessionType;
    }
    
    if ('duration' in dataToUpdate) dataToUpdate.duration = Number(dataToUpdate.duration) || 0;
    if ('wins' in dataToUpdate) dataToUpdate.wins = Number(dataToUpdate.wins) || 0;
    if ('losses' in dataToUpdate) dataToUpdate.losses = Number(dataToUpdate.losses) || 0;

    const res = await dataFetch(`update/sessions/${id}?owner_id=${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ ...dataToUpdate, user_id: userId })
    });
    
    if (res?.status === 'success') {
      setSessions(prev => prev.map(s => {
        if (String(s.id) === String(id)) {
          // Merge existing state, the updates, and the backend response
          const updated = {
            ...s,
            ...sessionData,
            ...(res.data || {})
          };
          
          updated.sessionType = updated.sessiontype || updated.sessionType || s.sessionType || 'Rolling';
          updated.duration = Number(updated.duration) || Number(s.duration) || 0;
          updated.wins = Number(updated.wins) || Number(s.wins) || 0;
          updated.losses = Number(updated.losses) || Number(s.losses) || 0;
          
          let parsedTechniques = updated.techniques || [];
          if (typeof updated.techniques === 'string') {
            try { 
              parsedTechniques = JSON.parse(updated.techniques); 
            } catch(e) {
              console.error("Failed to parse techniques on update", e);
            }
          }
          updated.techniques = Array.isArray(parsedTechniques) ? parsedTechniques : [];
          
          return updated;
        }
        return s;
      }));
    }
  };

  const deleteSession = async (id) => {
    const userId = localStorage.getItem('user_id');
    const res = await dataFetch(`delete/sessions/${id}?owner_id=${userId}`, {
      method: 'DELETE'
    });
    if (res?.status === 'success') {
      setSessions(prev => prev.filter(s => String(s.id) !== String(id)));
    }
  };

  const getSessionById = (id) => {
    return sessions.find(session => String(session.id) === String(id));
  };

  return (
    <SessionContext.Provider value={{ sessions, addSession, updateSession, deleteSession, getSessionById }}>
      {children}
    </SessionContext.Provider>
  );
};